// CASE-01/OPS-01 — small helpers shared by the exception inbox / case detail UI. Mirrors the
// style of cash.ts (money helpers) — pure functions, no React.

export function newRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // jsdom / very old browsers: a v4-shaped fallback is fine here — the server only requires a
  // syntactically valid UUID for idempotency, never cryptographic randomness.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const EXCEPTION_KINDS = [
  { value: "catalog_publication", label: "Catalog publication" },
  { value: "order_sync", label: "Order sync" },
  { value: "vendor_statement", label: "Vendor statement" },
  { value: "payout_attempt", label: "Payout attempt" },
  { value: "menu_shell_ownership", label: "Menu shell ownership" },
] as const;

export function operatorLabel(op: { display_name: string | null; email: string | null; id: string }): string {
  return op.display_name || op.email || `operator ${op.id.slice(0, 8)}`;
}

/** `<input type="datetime-local">` gives local wall-clock time with no offset — convert to an
 * ISO string with an explicit offset (backend requires timezone-aware). Empty input -> null. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** The inverse, for pre-filling a datetime-local input from a stored ISO deadline. */
export function isoToLocalInput(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── CASE-01 ownership-conflict resolution ───────────────────────────────────────────────────
// `app/services/menu/ownership_resolution.py::resolve_ownership_conflict` — a 200 response with
// `resolved: false` is a truthful, definitive non-outcome (NOT an error): either a refused fact
// (a real third-party owner, a missing/duplicate catalog row) that closes this attempt, or an
// unconfirmed remote observation that must be shown as unknown and never auto-retried. These sets
// are exhaustive over `ownership_resolution.py`'s reason vocabulary as of this writing; an
// unrecognized future code is treated as unknown (the conservative, never-auto-retry default).
export const OWNERSHIP_UNKNOWN_REASONS = new Set([
  "remote_owner_read_unknown",
  "remote_owner_digest_missing",
  "owner_readback_unknown",
  "owner_write_unknown",
]);

const OWNERSHIP_REFUSED_REASONS = new Set([
  "remote_owner_unexpected",
  "catalog_owner_row_missing",
  "requesting_vendor_row_already_exists",
]);

export type OwnershipOutcomeKind = "resolved" | "refused" | "unknown";

/** Classify a non-resolved `{resolved, reason}` result. Only call when `resolved` is `false`. */
export function ownershipOutcomeKind(reason: string | null): OwnershipOutcomeKind {
  if (reason !== null && OWNERSHIP_REFUSED_REASONS.has(reason)) return "refused";
  return "unknown";
}

/** HTTP-error-status codes from `resolve_ownership_conflict` that mean the case/conflict/
 * fingerprint the operator was looking at is no longer current — reload before retrying, never
 * silently resubmit stale expectations. */
const OWNERSHIP_STALE_CODES = new Set([
  "ownership_conflict_case_stale",
  "ownership_conflict_source_stale",
  "ownership_conflict_already_resolved",
  "ownership_conflict_not_open",
]);

export function isOwnershipStaleError(status: number, code: string): boolean {
  return status === 409 && OWNERSHIP_STALE_CODES.has(code);
}
